import { intl } from "dmsbasereact";
import { USER_TYPE_VALUES } from "./UserType";

/**
 * Category values from backend OrganizationCategoryEnum (LENA categories)
 * These values MUST match the backend enum in:
 * dmsbasebackend/dmsbasebackend/db/constants/organization_category.py
 *
 * Backend values:
 * - DISTRIBUTOR = 100
 * - DISPENSER = 101
 * - INTERMEDIARY = 102
 * - PRESCRIBER = 103
 * - BUYER = 104
 *
 * To fetch these dynamically, use the useConstants hook:
 * import { useOrganizationCategory } from '../hooks/useConstants';
 */
export const InstitutionCategoryEnum = {
  Distributor: 100,
  Dispenser: 101,
  Intermediary: 102,
  Prescriber: 103,
  Buyer: 104,
};

/**
 * Distributor category - special rules:
 * - When selected, automatically includes Buyer (Sales Channel)
 * - Can ONLY be combined with Buyer (no other categories allowed)
 * - Both Distributor and Buyer become locked when Distributor is selected
 */
export const DISTRIBUTOR_CATEGORY = InstitutionCategoryEnum.Distributor;

/**
 * Category that requires combination with others.
 * Buyer (104) cannot be selected alone.
 */
export const REQUIRES_COMBINATION_CATEGORY = InstitutionCategoryEnum.Buyer;


/**
 * Legacy export for backward compatibility
 * @deprecated Use DISTRIBUTOR_CATEGORY instead
 */
export const EXCLUSIVE_CATEGORY = DISTRIBUTOR_CATEGORY;

/**
 * Get category value from cached constants or fallback to static values
 */
export const getCategoryValue = (categoryName) => {
  // Try to get from localStorage cache first
  try {
    const cached = localStorage.getItem('app_constants');
    if (cached) {
      const constants = JSON.parse(cached);
      const category = constants.organization_category?.find(c => c.name === categoryName);
      if (category) return category.value;
    }
  } catch (e) {
    // Ignore cache errors, use fallback
  }
  // Fallback to static values
  return InstitutionCategoryEnum[categoryName] ?? null;
};

// Function to get labels at runtime (for proper i18n)
export const getInstitutionTypeLabel = (value) => {
  // Handle null, undefined, or empty string
  if (!value || value === "NONE" || value === "None" || value === "") {
    return "--";
  }

  const normalizedValue = String(value).toUpperCase();

  switch (normalizedValue) {
    case "CUSTOMER":
      return intl.get("Customer").d("Customer");
    case "SUPPLIER":
      return intl.get("Supplier").d("Supplier");
    case "SHIPPER":
      return intl.get("Shipper").d("Shipper");
    case "PARTNER":
      return intl.get("Partner").d("Partner");
    case "CLIENT":
      return intl.get("Client").d("Client");
    case "POLLUX":
      return intl.get("Pollux").d("Pollux");

    default:
      return value;
  }
};

// InstitutionTypeEnum with getter for labels to ensure i18n works at render time
export const InstitutionTypeEnum = {
  DISTRIBUTOR: {
    get label() { return intl.get("Distributor").d("Distributor"); },
    value: "DISTRIBUTOR",
    category: InstitutionCategoryEnum.Distributor,
  },
  DISPENSER: {
    get label() { return intl.get("Dispenser").d("Dispenser"); },
    value: "DISPENSER",
    category: InstitutionCategoryEnum.Dispenser,
  },
  INTERMEDIARY: {
    get label() { return intl.get("Intermediary").d("Intermediary"); },
    value: "INTERMEDIARY",
    category: InstitutionCategoryEnum.Intermediary,
  },
  PRESCRIBER: {
    get label() { return intl.get("Prescriber").d("Prescriber"); },
    value: "PRESCRIBER",
    category: InstitutionCategoryEnum.Prescriber,
  },
  BUYER: {
    get label() { return intl.get("SalesChannel").d("Sales Channel"); },
    value: "BUYER",
    category: InstitutionCategoryEnum.Buyer,
  },
};

/**
 * Get category options for select components
 * @returns {Array} Array of {value, label, labelKey, defaultLabel} objects
 */
export const getInstitutionCategoryOptions = () => {
  return Object.values(InstitutionTypeEnum)
    .map(type => ({
      value: type.category,
      label: type.label,
      labelKey: `category.${type.value.toLowerCase()}`,
      defaultLabel: type.label
    }));
};

/**
 * Get label for a category value
 * @param {number} categoryValue - Category value (e.g., 100)
 * @returns {string} Category label or "-" if not found/empty
 */
export const getInstitutionCategoryLabel = (categoryValue) => {
  // Handle null, undefined, 0, or empty values
  if (!categoryValue || categoryValue === 0 || categoryValue === "0") {
    return "-";
  }
  const entry = Object.values(InstitutionTypeEnum).find(t => t.category === categoryValue);
  return entry ? entry.label : "-";
};

/**
 * Find category entry by value
 * @param {number} categoryValue - Category value
 * @returns {Object|null} Category entry or null
 */
export const findCategoryByValue = (categoryValue) => {
  return Object.values(InstitutionTypeEnum).find(t => t.category === categoryValue) || null;
};

/**
 * @deprecated Use getInstitutionCategoryOptions() instead
 * Legacy format for backwards compatibility
 * Note: defaultLabel should be plain strings - intl.get() must be called at render time, not module load time
 */
export const INSTITUTION_CATEGORIES = [
  { value: InstitutionCategoryEnum.Distributor, labelKey: 'InstitutionCategory.Distributor', defaultLabel: 'Distributors' },
  { value: InstitutionCategoryEnum.Dispenser, labelKey: 'InstitutionCategory.Dispenser', defaultLabel: 'Dispensers' },
  { value: InstitutionCategoryEnum.Intermediary, labelKey: 'InstitutionCategory.Intermediary', defaultLabel: 'Intermediaries' },
  { value: InstitutionCategoryEnum.Prescriber, labelKey: 'InstitutionCategory.Prescriber', defaultLabel: 'Prescribers' },
  { value: InstitutionCategoryEnum.Buyer, labelKey: 'InstitutionCategory.Buyer', defaultLabel: 'Sales Channel' },
];

/**
 * Categories that have stock management features (Check-in/Check-out, Stock, Reconciliation)
 * Excludes Prescriber (103) as they don't manage physical stock
 */
export const STOCK_ENABLED_CATEGORIES = [
  InstitutionCategoryEnum.Distributor,   // 100
  InstitutionCategoryEnum.Dispenser,     // 101
  InstitutionCategoryEnum.Intermediary,  // 102
  InstitutionCategoryEnum.Buyer,         // 104
];

/**
 * Categories that can create prescriptions
 * Only Prescriber institutions can create prescriptions
 */
export const PRESCRIPTION_ENABLED_CATEGORIES = [
  InstitutionCategoryEnum.Prescriber,    // 103
];

/**
 * Categories that can perform dispensations
 * Only Dispenser institutions can dispense medications
 */
export const DISPENSATION_ENABLED_CATEGORIES = [
  InstitutionCategoryEnum.Dispenser,     // 101
];

/**
 * Check if a category has prescription features
 * @param {number} category - Category value
 * @returns {boolean} True if category can prescribe
 */
export const canPrescribe = (category) => PRESCRIPTION_ENABLED_CATEGORIES.includes(category);

/**
 * Check if a category has dispensation features
 * @param {number} category - Category value
 * @returns {boolean} True if category can dispense
 */
export const canDispense = (category) => DISPENSATION_ENABLED_CATEGORIES.includes(category);

/**
 * Check if a category has stock features
 * @param {number} category - Category value
 * @returns {boolean} True if category manages stock
 */
export const hasStockFeatures = (category) => STOCK_ENABLED_CATEGORIES.includes(category);

/**
 * Check if a category is distributor
 * @param {number} category - Category value
 * @returns {boolean} True if category is distributor
 */
export const isDistributorCategory = (category) => category === DISTRIBUTOR_CATEGORY;

/**
 * Check if a category is exclusive (cannot be combined with others)
 * @param {number} category - Category value
 * @returns {boolean} True if category is exclusive
 * @deprecated Use isDistributorCategory instead
 */
export const isExclusiveCategory = (category) => category === DISTRIBUTOR_CATEGORY;

/**
 * Check if a category combination is forbidden.
 * @param {number[]} categories - Array of category values
 * @returns {{ forbidden: boolean, message: string }}
 */
export const isForbiddenCategoryCombination = (categories) => {
  // Buyer + Prescriber alone is forbidden, but allowed when a third category is present
  const hasBuyer = categories.includes(InstitutionCategoryEnum.Buyer);
  const hasPrescriber = categories.includes(InstitutionCategoryEnum.Prescriber);
  if (hasBuyer && hasPrescriber) {
    const others = categories.filter(
      c => c !== InstitutionCategoryEnum.Buyer && c !== InstitutionCategoryEnum.Prescriber
    );
    if (others.length === 0) {
      return {
        forbidden: true,
        message: `${getInstitutionCategoryLabel(InstitutionCategoryEnum.Buyer)} cannot be combined with ${getInstitutionCategoryLabel(InstitutionCategoryEnum.Prescriber)} alone`
      };
    }
  }
  return { forbidden: false, message: '' };
};

/**
 * Check if Buyer would be left alone after removing a category.
 * @param {number[]} currentCategories - Currently selected categories
 * @param {number} categoryToRemove - Category being removed
 * @returns {boolean}
 */
export const wouldLeaveBuyerAlone = (currentCategories, categoryToRemove) => {
  if (!currentCategories.includes(REQUIRES_COMBINATION_CATEGORY)) {
    return false;
  }
  const remaining = currentCategories.filter(c => c !== categoryToRemove);
  return remaining.length === 1 && remaining[0] === REQUIRES_COMBINATION_CATEGORY;
};

/**
 * Check if selection contains Distributor
 * @param {number[]} categories - Category values
 * @returns {boolean}
 */
export const hasDistributor = (categories) => categories.includes(DISTRIBUTOR_CATEGORY);

/**
 * Validate category selection rules.
 * Rules:
 * - Distributor can ONLY be combined with Buyer (auto-selected, locked pair)
 * - Buyer cannot be selected alone
 * - Buyer + Prescriber is forbidden
 *
 * @param {number[]} selectedCategories - Currently selected categories
 * @param {number} newCategory - Category to be added
 * @returns {{ valid: boolean, message: string }} Validation result
 */
export const validateCategorySelection = (selectedCategories, newCategory) => {
  // If Distributor is already selected, no other category can be added
  // (Buyer is auto-selected with Distributor)
  if (hasDistributor(selectedCategories) && newCategory !== DISTRIBUTOR_CATEGORY && newCategory !== REQUIRES_COMBINATION_CATEGORY) {
    return {
      valid: false,
      message: 'Distributor can only be combined with Sales Channel'
    };
  }

  // If trying to add Distributor but already has categories other than Buyer
  if (isDistributorCategory(newCategory)) {
    const otherCategories = selectedCategories.filter(c => c !== REQUIRES_COMBINATION_CATEGORY);
    if (otherCategories.length > 0) {
      return {
        valid: false,
        message: 'Distributor can only be combined with Sales Channel'
      };
    }
  }

  // Check for forbidden combinations (Buyer + Prescriber)
  const potentialSelection = [...selectedCategories, newCategory];
  const forbiddenCheck = isForbiddenCategoryCombination(potentialSelection);
  if (forbiddenCheck.forbidden) {
    return {
      valid: false,
      message: forbiddenCheck.message
    };
  }

  return { valid: true, message: '' };
};

/**
 * Validate that Buyer is not alone in the selection.
 * @param {number[]} selectedCategories - Selected categories
 * @returns {{ valid: boolean, message: string }}
 */
export const validateBuyerNotAlone = (selectedCategories) => {
  if (selectedCategories.length === 1 && selectedCategories[0] === REQUIRES_COMBINATION_CATEGORY) {
    return {
      valid: false,
      message: 'Sales Channel must be combined with at least one other category'
    };
  }
  return { valid: true, message: '' };
};

/**
 * Get available categories based on current selection.
 * Rules:
 * - When Distributor is selected: only Distributor + Buyer visible (locked pair)
 * - When Buyer is selected without Distributor: hide Distributor, filter forbidden
 * - When nothing selected: all except Buyer (which requires another first)
 *
 * @param {number[]} selectedCategories - Currently selected categories
 * @returns {Object[]} Array of available category options
 */
export const getAvailableCategories = (selectedCategories) => {
  const allOptions = getInstitutionCategoryOptions();

  // If Distributor is selected, only show Distributor + Buyer (locked pair)
  if (hasDistributor(selectedCategories)) {
    return allOptions.filter(opt =>
      opt.value === DISTRIBUTOR_CATEGORY || opt.value === REQUIRES_COMBINATION_CATEGORY
    );
  }

  // If Buyer is selected (without Distributor), hide Distributor
  if (selectedCategories.includes(REQUIRES_COMBINATION_CATEGORY)) {
    let filtered = allOptions.filter(opt => opt.value !== DISTRIBUTOR_CATEGORY);

    // Also filter out options that would create forbidden combinations
    filtered = filtered.filter(opt => {
      if (selectedCategories.includes(opt.value)) return true;
      const potentialSelection = [...selectedCategories, opt.value];
      const { forbidden } = isForbiddenCategoryCombination(potentialSelection);
      return !forbidden;
    });

    return filtered;
  }

  // If any non-Distributor category is selected, hide only Distributor
  // Buyer remains available unless a forbidden combination would result (e.g. Buyer + Prescriber)
  if (selectedCategories.length > 0) {
    let filtered = allOptions.filter(opt => opt.value !== DISTRIBUTOR_CATEGORY);

    // Also filter out options that would create forbidden combinations
    filtered = filtered.filter(opt => {
      if (selectedCategories.includes(opt.value)) return true;
      const potentialSelection = [...selectedCategories, opt.value];
      const { forbidden } = isForbiddenCategoryCombination(potentialSelection);
      return !forbidden;
    });

    return filtered;
  }

  // Nothing selected: all except Buyer (which requires another to be selected first)
  return allOptions.filter(opt => opt.value !== REQUIRES_COMBINATION_CATEGORY);
};

/**
 * Check if a category is locked (cannot be removed) in current selection.
 * Buyer is locked when Distributor is selected.
 * @param {number[]} selectedCategories - Currently selected categories
 * @param {number} category - Category to check
 * @returns {boolean} True if category is locked
 */
export const isCategoryLocked = (selectedCategories, category) => {
  // Buyer is locked when Distributor is selected
  if (hasDistributor(selectedCategories) && category === REQUIRES_COMBINATION_CATEGORY) {
    return true;
  }
  return false;
};

/**
 * Get the initial/default categories for a user type.
 * Some user types have locked categories that are auto-selected.
 * @param {string} userType - User type value from USER_TYPE_VALUES
 * @returns {number[]} Array of default category values
 */
export const getDefaultCategoriesForUserType = (userType) => {
  switch (userType) {
    case USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE:
      // Distributor + Buyer (locked pair)
      return [InstitutionCategoryEnum.Distributor, InstitutionCategoryEnum.Buyer];
    default:
      return [];
  }
};

/**
 * Check if the category select should be locked/disabled for a user type.
 * @param {string} userType - User type value from USER_TYPE_VALUES
 * @returns {boolean} True if select should be completely locked
 */
export const isCategorySelectLocked = (userType) => {
  return userType === USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE;
};

/**
 * Get available categories based on user type and current selection.
 *
 * Rules by user type:
 * - DISTRIBUTOR_RESPONSIBLE: Only Distributor + Buyer (locked pair)
 * - All other user types (PRESCRIBER, DISPENSER, INSTITUTION_RESPONSIBLE): All categories except Distributor, with normal combination rules
 * - null/undefined (logged in admin): All with normal combination rules
 *
 * @param {number[]} selectedCategories - Currently selected categories
 * @param {string|null} userType - User type (null for logged-in admin flow)
 * @returns {Object[]} Array of available category options
 */
export const getAvailableCategoriesForUserType = (selectedCategories, userType) => {
  const allOptions = getInstitutionCategoryOptions();

  // If no userType, use normal rules (admin/gestão lena flow)
  if (!userType) {
    return getAvailableCategories(selectedCategories);
  }

  // DISTRIBUTOR_RESPONSIBLE: Only Distributor + Buyer (locked)
  if (userType === USER_TYPE_VALUES.DISTRIBUTOR_RESPONSIBLE) {
    return allOptions.filter(opt =>
      opt.value === InstitutionCategoryEnum.Distributor ||
      opt.value === InstitutionCategoryEnum.Buyer
    );
  }

  // PRESCRIBER, DISPENSER, INSTITUTION_RESPONSIBLE:
  // All categories except Distributor, with normal combination rules
  const withoutDistributor = selectedCategories.filter(c => c !== InstitutionCategoryEnum.Distributor);
  const available = getAvailableCategories(withoutDistributor);
  return available.filter(opt => opt.value !== InstitutionCategoryEnum.Distributor);
};
